import { IconX } from '@developerz/ui';
import { createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useDialogTrap } from '../lib/dialog-trap';
import { useClosing, useScrollLock } from '../lib/overlay';
import { buttonClass } from './ui/Button';

// Centered dialog (spec §3.5), drawn as the canvas panel: a head row (title
// left, the × right) over a rule, the body, and the ruled footer band.
// Open/close state lives in the parent; unmount is delayed by `useClosing` so
// the exit animation plays. Focus containment, Escape and focus return come from
// `useDialogTrap` (the × is the first control, so it takes initial focus); body
// scroll locks while open; overlay click closes.

export type ModalProps = {
  readonly open: boolean;
  readonly title: string;
  readonly desc?: string;
  /** Form dialogs: 32rem wide, roomier padding (spec §3.5 `--lg`). Wizards
   *  and other multi-step dialogs need the wider `--xl` (48rem) — the tab
   *  rail + state badges overflow `--lg` at narrow viewports. */
  readonly size?: 'lg' | 'xl';
  /** Rendered in the full-bleed footer band. */
  readonly footer?: JSX.Element;
  readonly onClose: () => void;
  readonly children: JSX.Element;
};

export function Modal(props: ModalProps): JSX.Element {
  let dialog: HTMLDivElement | undefined;
  useDialogTrap(
    () => props.open,
    () => dialog,
    () => props.onClose(),
  );
  useScrollLock(() => props.open);
  const gate = useClosing(() => props.open, 120);
  const descId = createUniqueId();

  return (
    <Show when={gate.visible()}>
      <div class="dialog" classList={{ 'dialog--closing': gate.closing() }}>
        <button
          type="button"
          class="dialog__overlay"
          tabindex="-1"
          aria-label={t('modal.dismiss')}
          onClick={props.onClose}
        />
        <div
          class="dialog__content"
          classList={{
            'dialog__content--lg': props.size === 'lg',
            'dialog__content--xl': props.size === 'xl',
          }}
          role="dialog"
          aria-modal="true"
          aria-label={props.title}
          aria-describedby={props.desc ? descId : undefined}
          tabindex="-1"
          ref={dialog}
        >
          <header class="dialog__header">
            <h2 class="dialog__title">{props.title}</h2>
            <button
              type="button"
              class={`${buttonClass({ variant: 'ghost', size: 'icon-sm' })} dialog__close`}
              aria-label={t('modal.close')}
              onClick={props.onClose}
            >
              <IconX size="1rem" />
            </button>
          </header>
          <Show when={props.desc}>
            <p id={descId} class="dialog__desc">
              {props.desc}
            </p>
          </Show>
          {props.children}
          <Show when={props.footer}>
            <div class="dialog__footer">{props.footer}</div>
          </Show>
        </div>
      </div>
    </Show>
  );
}
